import React, { useEffect } from 'react';
import { tx, tr } from '../../utils/i18n';
import { routePath } from '../../i18n/routes';
import { LoomEmbed } from '../LoomEmbed';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, FileText, Users, TrendingUp, Shield, Target, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Header from '../Header';
import Footer from '../Footer';
import { Toaster } from 'sonner';
import SEOHead from '../SEOHead';

const CommerceService = () => {
  const { t, currentLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Content Commerce Advertising",
    "description": "Intégrez vos publicités dans du contenu contextuel pertinent sur des blogs, sites d'actualités et plateformes de contenu. Native advertising avec intégrations Backmarket, Cirkul, Prohealth.",
    "provider": {
      "@type": "Organization",
      "name": "OptimHub",
      "url": "https://optimhub.com"
    },
    "areaServed": ["FR", "GB", "DE", "US"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Content Commerce Solutions",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Native Content Integration",
            "description": "Intégration native dans contenu éditorial"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Compliance Review",
            "description": "Vérification et conformité du contenu"
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "250"
    }
  };

  const features = [
    {
      icon: FileText,
      title: tx(currentLanguage, 'Contenu Contextuel', 'Contextual Content', 'Kontextueller Inhalt'),
      description: tx(currentLanguage, 'Intégration native dans du contenu éditorial pertinent pour votre audience', 'Native integration in editorial content relevant to your audience', 'Native Integration in redaktionelle Inhalte, die für Ihre Zielgruppe relevant sind')
    },
    {
      icon: Users,
      title: tx(currentLanguage, 'Audiences Niche', 'Niche Audiences', 'Nischen-Zielgruppen'),
      description: tx(currentLanguage, 'Ciblage précis d\'audiences spécialisées selon votre secteur d\'activité', 'Precise targeting of specialized audiences according to your industry', 'Präzise Ausrichtung auf spezialisierte Zielgruppen nach Ihrer Branche')
    },
    {
      icon: Shield,
      title: tx(currentLanguage, 'Intégration Native', 'Native Integration', 'Native Integration'),
      description: tx(currentLanguage, 'Publicités non-intrusives qui s\'intègrent naturellement au contenu', 'Non-intrusive ads that naturally integrate with content', 'Nicht aufdringliche Anzeigen, die sich natürlich in den Inhalt einfügen')
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: tx(currentLanguage, 'Engagement Élevé', 'High Engagement', 'Hohes Engagement'),
      description: tx(currentLanguage, 'Taux d\'engagement 5x supérieur grâce à l\'intégration native', '5x higher engagement rate thanks to native integration', '5x höhere Engagement-Rate dank nativer Integration')
    },
    {
      icon: TrendingUp,
      title: tx(currentLanguage, 'Brand Awareness', 'Brand Awareness', 'Markenbewusstsein'),
      description: tx(currentLanguage, 'Augmentation de la notoriété de marque via du contenu de qualité', 'Increase brand awareness through quality content', 'Steigerung des Markenbewusstseins durch qualitativ hochwertige Inhalte')
    },
    {
      icon: Zap,
      title: tx(currentLanguage, 'Performance Durable', 'Sustainable Performance', 'Nachhaltige Leistung'),
      description: tx(currentLanguage, 'Résultats à long terme grâce à la confiance des utilisateurs', 'Long-term results thanks to user trust', 'Langzeitergebnisse dank Nutzervertrauen')
    }
  ];

  const stats = [
    { value: '92%', label: tx(currentLanguage, 'Taux de Confiance', 'Trust Rate', 'Vertrauensrate') },
    { value: '150M+', label: tx(currentLanguage, 'Lecteurs Mensuels', 'Monthly Readers', 'Monatliche Leser') },
    { value: '4.8x', label: tx(currentLanguage, 'Engagement vs Display', 'Engagement vs Display', 'Engagement vs Display') }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={tr(currentLanguage, "Content Commerce Advertising | Native Ads & Editorial Integration - OptimHub")}
        description={tr(currentLanguage, "Solution Content Commerce leader : intégration native dans blogs, newsletters et sites éditoriaux. +150M lecteurs mensuels, 92% taux de confiance. CPC vers CPA garanti.")}
        keywords="content commerce, commerce content, native advertising, editorial content ads, blog advertising, newsletter advertising, contextual advertising, brand safe content, content integration, compliance advertising, native ads, sponsored content"
        ogImage="https://images.unsplash.com/photo-1576864333214-b2cee50f8283?w=1200"
        routeKey="serviceCommerce"
        lang={currentLanguage}
        type="article"
        schemaData={schemaData}
      />
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
                {tx(currentLanguage, 'Contenu Commerce', 'Commerce Content', 'Commerce-Inhalt')}
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Commerce Content
                <span className="block text-blue-600 mt-2">
                  {tx(currentLanguage, 'Advertising', 'Advertising', 'Werbung')}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {tx(currentLanguage, 'Intégrez vos publicités dans du contenu contextuel pertinent sur des blogs, sites d\'actualités et plateformes de contenu pour une expérience utilisateur optimale. En tant que traffic brokers, nous achetons du trafic au CPC et vous ne payez qu\'au CPA.', 'Integrate your ads into relevant contextual content on blogs, news sites and content platforms for an optimal user experience. As traffic brokers, we buy traffic at CPC and you only pay at CPA.', 'Integrieren Sie Ihre Anzeigen in relevante kontextuelle Inhalte auf Blogs, Nachrichtenseiten und Content-Plattformen für eine optimale Nutzererfahrung. Als Traffic-Broker kaufen wir Traffic zu CPC und Sie zahlen nur zu CPA.')
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Commencer maintenant', 'Get Started', 'Jetzt starten')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="mailto:contact@optimhub.com?subject=Demande de démo Commerce Content"
                  className="border-2 border-blue-200 text-blue-700 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 inline-flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Voir la démo', 'Watch Demo', 'Demo ansehen')}
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1576864333214-b2cee50f8283?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxuYXRpdmUlMjBhZHZlcnRpc2luZ3xlbnwwfHx8Ymx1ZXwxNzYxNTc3MzAxfDA&ixlib=rb-4.1.0&q=85"
                alt="Commerce Content Advertising"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Placements Contenu Premium', 'Premium Content Placements', 'Premium-Content-Platzierungen')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {tx(currentLanguage, 'Atteignez vos audiences avec des publicités natives intégrées dans du contenu éditorial de qualité.', 'Reach your audiences with native ads integrated into quality editorial content.', 'Erreichen Sie Ihre Zielgruppen mit nativen Anzeigen, die in hochwertigen redaktionellen Inhalt integriert sind.')
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed text-center">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Live Examples Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Exemples en Direct', 'Live Examples', 'Live-Beispiele')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {tx(currentLanguage, 'Découvrez comment nos campagnes Commerce Content s\'intègrent naturellement dans le contenu éditorial de nos partenaires.', 'See how our Commerce Content campaigns integrate naturally into our partners\' editorial content.', 'Sehen Sie, wie sich unsere Commerce Content-Kampagnen natürlich in die redaktionellen Inhalte unserer Partner integrieren.')
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Example 1 - Python Central */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">Backmarket (GB) × pythoncentral.io</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Content Commerce Integration</h3>
                <p className="text-gray-600 text-sm mb-4">Native integration in tech editorial content</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="6412421e286b435bb32a5bd4aea54cc7" />
              </div>
            </div>

            {/* Example 2 - Milled.com GB */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">Backmarket (GB) × milled.com</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Email Content Integration</h3>
                <p className="text-gray-600 text-sm mb-4">Strategic placement in newsletter content</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="eb2bf944fe1942d0a5be9b131c348b5a" />
              </div>
            </div>

            {/* Example 3 - Milled.com FR */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">Back Market (FR) × milled.com</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Compliance Integration</h3>
                <p className="text-gray-600 text-sm mb-4">Brand-safe content compliance</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="0df892097029443a82e34ad93922e9d1" />
              </div>
            </div>

            {/* Example 4 - Listful.com */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">Back Market (FR) × listful.com</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">List Content Integration</h3>
                <p className="text-gray-600 text-sm mb-4">Native placement in curated lists</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="e46a873107a14e2d8ba2ffdf33e2b2dd" />
              </div>
            </div>

            {/* Example 5 - Cirkul on shopcheck.net */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">Cirkul × shopcheck.net</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Compliance Review</h3>
                <p className="text-gray-600 text-sm mb-4">Content supply type compliance</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="ff4505e24c7845279d843d62218ede41" />
              </div>
            </div>

            {/* Example 6 - LatestBedding on milled.com */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">LatestBedding × milled.com</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Content Commerce Compliance</h3>
                <p className="text-gray-600 text-sm mb-4">Brand-safe content integration</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="59990122b06a427f87ce481b72c30779" />
              </div>
            </div>

            {/* Example 7 - Prohealth on milled.com */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">Prohealth × milled.com</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Content Commerce Compliance</h3>
                <p className="text-gray-600 text-sm mb-4">Health & wellness content integration</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="bbcd0bd7dd1f438ea7b56af9fa8feef7" />
              </div>
            </div>

            {/* Example 8 - AttractionTickets on foroactivo.com */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">AttractionTickets × foroactivo.com</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Compliance Review</h3>
                <p className="text-gray-600 text-sm mb-4">Content integration in community forums</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="a3c2a24114784d178c0e0f99e9eb5a0e" />
              </div>
            </div>

            {/* Example 9 - myrevair US on sassytownhouseliving.com */}
            <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4">
                <div className="text-sm text-blue-600 font-semibold mb-2">myrevair US × sassytownhouseliving.com</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Content Commerce Compliance</h3>
                <p className="text-gray-600 text-sm mb-4">Lifestyle content integration</p>
              </div>
              <div className="relative bg-white rounded-lg overflow-hidden">
                <LoomEmbed id="15e58afeda854d7b852e58898e51c2b8" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Pourquoi Commerce Content ?', 'Why Commerce Content?', 'Warum Commerce Content?')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {tx(currentLanguage, 'Prêt à Engager Vos Audiences ?', 'Ready to Engage Your Audiences?', 'Bereit, Ihre Zielgruppen zu engagieren?')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            {tx(currentLanguage, 'Rejoignez des centaines de marques qui font confiance à notre réseau Commerce Content pour leur growth.', 'Join hundreds of brands that trust our Commerce Content network for their growth.', 'Schließen Sie sich Hunderten von Marken an, die unserem Commerce Content-Netzwerk für ihr Wachstum vertrauen.')
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
            >
              {tx(currentLanguage, 'Démarrer maintenant', 'Start Now', 'Jetzt starten')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <a 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200 cursor-pointer"
            >
              {tx(currentLanguage, 'Parler à un expert', 'Talk to an Expert', 'Mit Experten sprechen')}
            </a>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  );
};

export default CommerceService;
